import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

// Customers always get the branded address, never the hosting URL this console happens to be open on
const PUBLIC_LOGIN_URL = 'https://www.supplytrack.co.nz/login';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blank = (d) => ({
  name: '', owner_name: '', email: '', password: '', store1: '', store2: '',
  price_per_store: d?.default_price_per_store ?? 300, onboarding_fee: d?.default_onboarding_fee ?? 1000,
  gst_number: '', notes: '', copy_catalogue: true,
  trial: false, trial_days: 2, demo_data: true,
  send_invite: true, personal_note: '',
});

export default function NewBusinessDialog({ open, onClose, onCreated, defaults }) {
  const { getAuthHeader } = useAuth();
  const [f, setF] = useState(blank(defaults));
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const close = () => { setF(blank(defaults)); setResult(null); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    const stores = [f.store1, f.store2].map(s => s.trim()).filter(Boolean);
    if (!stores.length) return toast.error('Add at least one store name');
    setSaving(true);
    try {
      const r = await axios.post(`${API}/platform/businesses`, {
        name: f.name.trim(), owner_name: f.owner_name.trim(), email: f.email.trim().toLowerCase(), password: f.password,
        stores, price_per_store: Number(f.price_per_store), onboarding_fee: Number(f.onboarding_fee),
        gst_number: f.gst_number, notes: f.notes, copy_catalogue: f.copy_catalogue,
        trial: f.trial, trial_days: Number(f.trial_days || 1), demo_data: f.demo_data,
        send_invite: f.send_invite, login_url: PUBLIC_LOGIN_URL, personal_note: f.personal_note || null,
      }, { headers: getAuthHeader() });
      setResult(r.data);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create business');
    } finally {
      setSaving(false);
    }
  };

  const monthly = Number(f.price_per_store || 0) * [f.store1, f.store2].filter(s => s.trim()).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="new-business-dialog">
        <DialogHeader>
          <DialogTitle>{f.trial ? 'New trial / demo login' : 'New customer business'}</DialogTitle>
          <DialogDescription>Creates their owner login, stores and a copy of your catalogue (with your prices — they can change theirs).</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="new-business-result">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900">
              <p className="font-semibold text-base mb-1">{result.business.name} is ready{result.business.is_trial ? ' (trial)' : ''}</p>
              <p>Catalogue: {result.catalogue ? `${result.catalogue.products_copied.toLocaleString()} products, ${result.catalogue.suppliers_copied} suppliers` : 'not copied'}</p>
              {result.demo && <p>Demo data: {result.demo.stocked} products stocked, {result.demo.sales} sample sales, {result.demo.customers} customers</p>}
              {result.business.is_trial
                ? <p data-testid="trial-ends-note">Trial ends {new Date(result.business.trial_ends_at).toLocaleString('en-NZ')} — no invoices until you convert them.</p>
                : <p>Monthly fee: ${result.business.monthly_fee.toFixed(2)} + GST · Onboarding invoice: ${(Number(result.business.onboarding_fee || 0) * 1.15).toFixed(2)} incl GST (due)</p>}
            </div>
            {result.invite && (
              <p className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900" data-testid="invite-sent-note">
                Invite email sent to <b>{result.invite.sent_to}</b> with the login, {result.business.is_trial ? 'trial end date' : 'account details'} and your pricing. You were BCC'd.
              </p>
            )}
            <div className="rounded-lg border p-4 text-sm space-y-1">
              <p className="font-medium">{result.invite ? 'Login details (also in the email)' : 'Send them these login details'}</p>
              <p>Web: <span className="font-mono">{PUBLIC_LOGIN_URL}</span></p>
              <p>Email: <span className="font-mono">{f.email}</span></p>
              <p>Password: <span className="font-mono">{f.password}</span></p>
              <p className="text-gray-500 text-xs pt-1">They sign in as the manager, then add staff PINs under Staff. Stores: {[f.store1, f.store2].filter(s => s.trim()).join(', ')}.</p>
            </div>
            <Button onClick={close} className="w-full" data-testid="new-business-done">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-sky-900">
                <input type="checkbox" checked={f.trial} onChange={set('trial')} data-testid="nb-trial" />
                Trial / demo login — let them try the POS before they decide (no invoices)
              </label>
              {f.trial && (
                <div className="flex flex-wrap items-center gap-4 text-sm text-sky-900 pl-6">
                  <span className="flex items-center gap-2">Locks after
                    <Input type="number" min="1" max="60" value={f.trial_days} onChange={set('trial_days')} className="w-16 h-8" data-testid="nb-trial-days" /> day(s)
                  </span>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={f.demo_data} onChange={set('demo_data')} data-testid="nb-demo-data" />
                    realistic sample stock + sales
                  </label>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Business name" id="nb-name"><Input id="nb-name" value={f.name} onChange={set('name')} required placeholder="Highbury Health" data-testid="nb-name" /></Field>
              <Field label="Owner / manager name" id="nb-owner"><Input id="nb-owner" value={f.owner_name} onChange={set('owner_name')} required data-testid="nb-owner" /></Field>
              <Field label="Login email" id="nb-email"><Input id="nb-email" type="email" value={f.email} onChange={set('email')} required data-testid="nb-email" /></Field>
              <Field label="Initial password" id="nb-pw"><Input id="nb-pw" value={f.password} onChange={set('password')} required minLength={6} data-testid="nb-password" /></Field>
              <Field label="Store 1 name" id="nb-s1"><Input id="nb-s1" value={f.store1} onChange={set('store1')} required placeholder="Highbury" data-testid="nb-store1" /></Field>
              <Field label="Store 2 name (optional)" id="nb-s2"><Input id="nb-s2" value={f.store2} onChange={set('store2')} placeholder="leave blank for one store" data-testid="nb-store2" /></Field>
              <Field label="Fee per store / month (NZD, ex GST)" id="nb-fee"><Input id="nb-fee" type="number" step="1" min="0" value={f.price_per_store} onChange={set('price_per_store')} data-testid="nb-fee" /></Field>
              <Field label="Onboarding fee (one-off, ex GST)" id="nb-onb"><Input id="nb-onb" type="number" step="1" min="0" value={f.onboarding_fee} onChange={set('onboarding_fee')} data-testid="nb-onboarding" /></Field>
              <Field label="GST number (optional)" id="nb-gst"><Input id="nb-gst" value={f.gst_number} onChange={set('gst_number')} /></Field>
            </div>
            <Field label="Notes (bank details sent, contract, etc.)" id="nb-notes"><Textarea id="nb-notes" rows={2} value={f.notes} onChange={set('notes')} /></Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.copy_catalogue} onChange={set('copy_catalogue')} data-testid="nb-copy" />
              Copy my full catalogue incl. prices (recommended — they sell the same products)
            </label>
            <div className="rounded-lg border p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={f.send_invite} onChange={set('send_invite')} data-testid="nb-send-invite" />
                Email them the login details now (SupplyTrack welcome email with pricing, replies come to you)
              </label>
              {f.send_invite && (
                <Textarea rows={2} value={f.personal_note} onChange={set('personal_note')} placeholder="Optional personal note, e.g. 'Great chatting on Tuesday — have a play and call me with any questions.'" data-testid="nb-personal-note" />
              )}
            </div>
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-gray-600">
                {f.trial ? <>Quote if they sign: </> : null}Monthly: <b>${monthly.toFixed(2)} + GST</b> · Onboarding: <b>${Number(f.onboarding_fee || 0).toFixed(2)} + GST</b>
              </p>
              <Button type="submit" disabled={saving} className="bg-[#1B5E20] hover:bg-[#2E7D32]" data-testid="nb-submit">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up…</> : (f.trial ? 'Create trial login' : 'Create business')}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, id, children }) => (
  <div className="space-y-1"><Label htmlFor={id} className="text-xs text-gray-600">{label}</Label>{children}</div>
);
